import { useEffect, useMemo, useState } from 'react';
import { customerAdminApi } from '@/api/customerAdmin';
import type { AdminStats, TenantDetail, TenantSummary } from '@/api/types';
import { ApiError } from '@/lib/api';

function statusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'text-[#39FF14]';
    case 'trial':
    case 'trialing':
      return 'text-yellow-400';
    case 'suspended':
    case 'cancelled':
    case 'canceled':
    case 'churned':
      return 'text-red-400';
    default:
      return 'text-gray-500';
  }
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '—';
  }
}

function formatNumber(value: number | null | undefined): string {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

function tenantKey(tenant: TenantSummary): string {
  return tenant.tenant_id || tenant.id;
}

function isMissingContract(error: unknown): boolean {
  return error instanceof ApiError && [404, 405, 501].includes(error.status);
}

function detailFallback(tenant: TenantSummary): TenantDetail {
  return {
    ...tenant,
    projects: [],
    recent_credit_transactions: [],
    recent_subscription_events: [],
    recent_communications: [],
    recent_status_transitions: [],
    recent_waitlist_actions: [],
  };
}

export function AdminTab() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<TenantDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.allSettled([
      customerAdminApi.getStats(),
      customerAdminApi.listTenants({ limit: 200 }),
    ])
      .then(([statsResult, tenantsResult]) => {
        if (cancelled) return;

        if (statsResult.status === 'fulfilled') {
          setStats(statsResult.value);
        } else {
          setStats(null);
        }

        if (tenantsResult.status === 'fulfilled') {
          const nextTenants = Array.isArray(tenantsResult.value.tenants)
            ? tenantsResult.value.tenants
            : [];
          setTenants(nextTenants);
          setSelectedTenant(nextTenants[0] ? detailFallback(nextTenants[0]) : null);
          setDetailError(nextTenants[0] ? 'Tenant detail endpoint not verified yet; showing list data.' : null);
        } else {
          setError(tenantsResult.reason instanceof Error ? tenantsResult.reason.message : 'Failed to load tenants');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => ({
    active: tenants.filter((t) => (t.subscription_status ?? t.status).toLowerCase() === 'active').length,
    trial: tenants.filter((t) => ['trial', 'trialing'].includes((t.subscription_status ?? t.status).toLowerCase())).length,
    churned: tenants.filter((t) =>
      ['suspended', 'cancelled', 'canceled', 'churned'].includes((t.subscription_status ?? t.status).toLowerCase()),
    ).length,
  }), [tenants]);

  const loadTenantDetail = async (tenant: TenantSummary) => {
    setSelectedTenant(detailFallback(tenant));
    setDetailError(null);
    setActionMessage(null);
    setActionError(null);
    try {
      const detail = await customerAdminApi.getTenantDetail(tenantKey(tenant));
      setSelectedTenant({ ...detailFallback(tenant), ...detail });
    } catch (err) {
      if (isMissingContract(err)) {
        setDetailError('Tenant detail endpoint is not available through the current engine bridge; showing list data.');
        return;
      }
      setDetailError(err instanceof Error ? err.message : 'Failed to load tenant detail');
    }
  };

  const refreshSelectedTenant = async (tenantId: string) => {
    try {
      const detail = await customerAdminApi.getTenantDetail(tenantId);
      setSelectedTenant(detail);
      setTenants((current) =>
        current.map((tenant) =>
          tenantKey(tenant) === tenantId
            ? { ...tenant, ...detail }
            : tenant,
        ),
      );
      setDetailError(null);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to refresh tenant detail');
    }
  };

  const runTenantAction = async (
    actionKey: string,
    successMessage: string,
    action: (tenantId: string) => Promise<unknown>,
  ) => {
    if (!selectedTenant) return;
    const id = tenantKey(selectedTenant);
    setActionLoading(actionKey);
    setActionMessage(null);
    setActionError(null);
    try {
      await action(id);
      setActionMessage(successMessage);
      await refreshSelectedTenant(id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-6 font-mono text-sm text-gray-200 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Customers/Tenants</div>
          <div className="text-[11px] text-gray-600 mt-1">
            Contract-first recovery: list is live; detail/actions degrade honestly if the engine bridge is missing endpoints.
          </div>
        </div>
        {tenants.length > 0 && (
          <div className="text-xs text-gray-500">
            <span className="text-gray-300">{formatNumber(stats?.total_tenants ?? tenants.length)}</span> total ·{' '}
            <span className="text-[#39FF14]">{counts.active}</span> active ·{' '}
            <span className="text-yellow-400">{counts.trial}</span> trial ·{' '}
            <span className="text-red-400">{counts.churned}</span> churned
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-gray-500">Loading tenants…</p>
      ) : error ? (
        <div className="text-red-400 text-xs">
          <p>Failed to load /api/v1/admin/tenants</p>
          <p className="text-gray-500 mt-1">{error}</p>
        </div>
      ) : tenants.length === 0 ? (
        <p className="text-gray-500">No tenants found.</p>
      ) : (
        <div className="grid grid-cols-[minmax(520px,1fr)_420px] gap-4 min-h-0 flex-1">
          <div className="overflow-auto border border-gray-800 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-900/90 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Tenant</th>
                  <th className="text-left px-3 py-2 font-normal">Admin</th>
                  <th className="text-left px-3 py-2 font-normal">Status</th>
                  <th className="text-left px-3 py-2 font-normal">Tier</th>
                  <th className="text-left px-3 py-2 font-normal">Created</th>
                  <th className="text-left px-3 py-2 font-normal">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const statusLabel = t.subscription_status ?? t.status;
                  const active = selectedTenant && tenantKey(selectedTenant) === tenantKey(t);
                  return (
                    <tr
                      key={t.id}
                      className={`border-t border-gray-800 hover:bg-gray-900/40 cursor-pointer ${active ? 'bg-gray-900/60' : ''}`}
                      onClick={() => void loadTenantDetail(t)}
                    >
                      <td className="px-3 py-2 text-gray-200">
                        {t.display_name || t.tenant_id || t.id}
                      </td>
                      <td className="px-3 py-2 text-gray-400">{t.admin_email ?? '—'}</td>
                      <td className={`px-3 py-2 ${statusColor(statusLabel)}`}>{statusLabel}</td>
                      <td className="px-3 py-2 text-gray-400 uppercase tracking-wider text-[10px]">
                        {t.tier ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{formatDate(t.created_at)}</td>
                      <td className="px-3 py-2 text-gray-500">{formatDate(t.last_activity)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <aside className="border border-gray-800 rounded bg-black/20 p-4 overflow-auto">
            {!selectedTenant ? (
              <p className="text-gray-500 text-xs">Select a customer to inspect details.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-600">Selected tenant</div>
                  <div className="text-lg text-gray-100 mt-1">
                    {selectedTenant.display_name || selectedTenant.tenant_id || selectedTenant.id}
                  </div>
                  <div className="text-xs text-gray-500">{selectedTenant.admin_email ?? 'No admin email'}</div>
                </div>

                {detailError && (
                  <div className="border border-yellow-500/30 bg-yellow-500/5 text-yellow-300 text-xs rounded p-3">
                    {detailError}
                  </div>
                )}
                {actionMessage && (
                  <div className="border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs rounded p-3">
                    {actionMessage}
                  </div>
                )}
                {actionError && (
                  <div className="border border-red-500/30 bg-red-500/5 text-red-300 text-xs rounded p-3">
                    {actionError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="border border-gray-800 rounded p-2">
                    <div className="text-gray-600 uppercase tracking-wider text-[10px]">Status</div>
                    <div className={statusColor(selectedTenant.status)}>{selectedTenant.status}</div>
                  </div>
                  <div className="border border-gray-800 rounded p-2">
                    <div className="text-gray-600 uppercase tracking-wider text-[10px]">Tier</div>
                    <div className="text-gray-300 uppercase">{selectedTenant.tier ?? '—'}</div>
                  </div>
                  <div className="border border-gray-800 rounded p-2">
                    <div className="text-gray-600 uppercase tracking-wider text-[10px]">Subscription</div>
                    <div className="text-gray-300">{selectedTenant.subscription_status ?? '—'}</div>
                  </div>
                  <div className="border border-gray-800 rounded p-2">
                    <div className="text-gray-600 uppercase tracking-wider text-[10px]">Beta access</div>
                    <div className="text-gray-300">{selectedTenant.beta_full_access ? 'enabled' : 'not verified'}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-600">Actions</div>
                  <button
                    type="button"
                    disabled={actionLoading !== null}
                    onClick={() => void runTenantAction(
                      'activate',
                      'Tenant activated.',
                      (id) => customerAdminApi.updateTenantStatus(id, { status: 'active', reason: 'Director Command Center action' }),
                    )}
                    className="w-full px-3 py-2 text-left text-xs border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
                  >
                    {actionLoading === 'activate' ? 'Activating...' : 'Activate tenant'}
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading !== null}
                    onClick={() => void runTenantAction(
                      'inactive',
                      'Tenant marked inactive.',
                      (id) => customerAdminApi.updateTenantStatus(id, { status: 'inactive', reason: 'Director Command Center action' }),
                    )}
                    className="w-full px-3 py-2 text-left text-xs border border-yellow-500/30 rounded text-yellow-300 hover:bg-yellow-500/10 disabled:opacity-50"
                  >
                    {actionLoading === 'inactive' ? 'Marking inactive...' : 'Mark inactive'}
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading !== null}
                    onClick={() => void runTenantAction(
                      'beta',
                      selectedTenant.beta_full_access ? 'Beta full-access disabled.' : 'Beta full-access enabled.',
                      (id) => customerAdminApi.updateTenantBetaAccess(id, {
                        enabled: !selectedTenant.beta_full_access,
                        reason: 'Director Command Center action',
                      }),
                    )}
                    className="w-full px-3 py-2 text-left text-xs border border-blue-500/30 rounded text-blue-300 hover:bg-blue-500/10 disabled:opacity-50"
                  >
                    {actionLoading === 'beta'
                      ? 'Updating beta access...'
                      : selectedTenant.beta_full_access
                        ? 'Disable beta full-access'
                        : 'Enable beta full-access'}
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading !== null || !selectedTenant.admin_email}
                    onClick={() => void runTenantAction(
                      'email',
                      'Access email sent.',
                      (id) => customerAdminApi.sendAccessEmail(id),
                    )}
                    className="w-full px-3 py-2 text-left text-xs border border-gray-700 rounded text-gray-300 hover:bg-gray-900 disabled:text-gray-600 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'email' ? 'Sending email...' : 'Send / resend access email'}
                  </button>
                  <div className="text-[10px] text-gray-600">
                    API-key reveal remains deferred; use show-once provision/rotate later.
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-600">Recent history</div>
                  <div className="text-xs text-gray-500">
                    Communications: {selectedTenant.recent_communications?.length ?? 0} · Status changes:{' '}
                    {selectedTenant.recent_status_transitions?.length ?? 0} · Waitlist actions:{' '}
                    {selectedTenant.recent_waitlist_actions?.length ?? 0}
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
